import { PartnerFormStyle } from "@xapp/stentor-form-widget-channel";

/**
 * CostGuide's approved form look, as a named style bundle.
 *
 * ONE definition, exported so every consumer reads the same values: the code
 * fallback in contact-app's partner registry, and the Storybook story that shows
 * the look. Duplicating it drifts -- the story and the registry had already
 * diverged on the button colours, which is how a CostGuide-preset form ended up
 * rendering a stock magenta Continue button inside a teal frame.
 *
 * Once `public.partnerBranding.formStyle` ships (XappMedia/stentor-api#5753) the
 * API-served bundle wins and this is only reached when that fetch fails.
 *
 * Colour notes, all from the approved design:
 *  - #1aacbe is the frame border, the buttons and the selected chip/date state.
 *  - #22a2b4 is the tab -- deliberately a DIFFERENT teal from the border.
 *  - #eef8fa is the card interior the framed step sits on.
 */
export const COST_GUIDE_FORM_STYLE: PartnerFormStyle = {
    enabled: true,
    theme: {
        // No visible container. The widget root paints a border in this colour, and with the
        // frame and tab gone it was the last box left around a form that should sit flat on the
        // page -- their own form has no outer border at all. Transparent rather than removing
        // the rule, so the layout keeps the same box metrics.
        borderColor: "transparent",
        // The approved design's inputs and buttons are slightly squarer than stock.
        // The frame's own card is square regardless of this -- see FormFrame.
        borderRadius: "8px",
        // White, not the pale teal card interior. That tint existed to separate the framed card
        // from the page behind it; with no frame it just makes the form look like a panel.
        backgroundColor: "#ffffff",
        // The step header was a teal bar with white text, which read as a tab even once the real
        // tab was gone. On white with dark text it reads as a heading, which is what it is.
        headerBackgroundColor: "#ffffff",
        headerTextColor: "#1c3f47",
        // CostGuide's own advance button is orange, not teal -- `.btn-orange {background:#fb9b26}`
        // in their main.css, which `.btn-next` uses. Teal was our reading of their older design.
        primaryButtonColor: "#fb9b26",
        primaryButtonTextColor: "#ffffff",
        // BOTH buttons, because a step that sets `nextLabel` routes to the SECONDARY one -- and
        // every step of the CostGuide form sets one ("Next: Contact Info", "Next: book your
        // estimate"). Setting only the primary leaves the button the visitor actually presses
        // its stock magenta.
        secondaryButtonColor: "#fb9b26",
        secondaryButtonTextColor: "#ffffff",
        text: {
            color: "#1c3f47",
            // The fill was never claimed here, so it fell through to the customer's own theme --
            // Erie ships `rgba(0, 0, 0, 0.06)`, a grey wash that reads as disabled next to
            // CostGuide's white fields. A pale tint of their own palette instead. The hover and
            // focus shades derive from this automatically (`darken(backgroundColor, 3)`).
            //
            // Not white: without a border on the input a white field is invisible on the white
            // form. Their fields are white WITH a 1px border, which we cannot match until the
            // input border is themable -- `border: 0 !important` is hardcoded in
            // FormFieldTextInput.module.scss.
            backgroundColor: "#ffffff",
            // Outlined fields, now that the border is themable. Their own:
            // `.main-form input:focus {border:2px solid #92dae1}`, and the resting border is the
            // same family a shade lighter. White fill is safe once there is a border to read.
            borderColor: "#cfe3e7",
            borderWidth: "1px",
            borderColorFocus: "#92dae1",
        },
        chips: {
            // Both states. Only the selected pair was set, so an UNSELECTED chip fell through to
            // the customer's own theme -- on book.xapp.ai that rendered American Dream's maroon
            // pills inside an otherwise entirely teal form. A chip spends most of its life
            // unselected, so it was the resting state that looked wrong.
            //
            // Same class as the stock magenta Continue button this file's header records: a
            // themable surface the preset does not claim is a surface the customer keeps.
            backgroundColor: "#d6eef2",
            color: "#1c3f47",
            backgroundColorSelected: "#1aacbe",
            colorSelected: "#ffffff",
        },
        date: {
            backgroundColorSelected: "#1aacbe",
        },
        // Their advance button is chunky: 20px bold on a 10px radius with a heavy drop shadow
        // (`.btn-next {padding:17px 25px; font-size:20px; font-weight:700; border-radius:10px;
        // box-shadow:0 4px 5px 4px rgba(0,0,0,.15)}`). Ours was a stock MUI button wearing
        // their orange -- right colour, half the presence.
        button: {
            fontSize: "20px",
            fontWeight: "700",
            padding: "17px 25px",
            borderRadius: "10px",
            boxShadow: "0 4px 5px 4px rgba(0, 0, 0, 0.15)",
        },

        // Back and submit on one row, as theirs are -- the circle on the left, the button beside
        // it -- rather than submit centred above with back stranded underneath.
        finalButtonPlacement: "inline",

        // Their step heading: centred, 24px, dark teal (`.main-form .ms-title {font-size:24px;
        // color:#174146}`). Ours was left-aligned body text, which read as a label.
        stepTitle: {
            align: "center",
            fontSize: "24px",
            fontWeight: "700",
            color: "#174146",
        },

        // An icon-only circle with an arrow, which is what their back control is --
        // `.btn-back svg {fill:#a0cdd3}`, hover `#174146` on both the ring and the arrow.
        backButtonVariant: "icon",
        backButton: {
            color: "#a0cdd3",
            borderColor: "#a0cdd3",
            colorHover: "#174146",
            // 1px read as hairline next to a 20px bold button beside it.
            borderWidth: "2px",
            iconStroke: "0.8px",
        },

        font: {
            // Poppins, which is what their own page loads: `family=Poppins:300,400,700` on
            // match.costguide.com. Montserrat came from their embed loader, which still requests
            // it -- but the page the visitor compares us against is set in Poppins.
            fontFamily: "Poppins, Arial, Verdana, sans-serif",
            link: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;700&display=swap",
        },
        standAlone: {
            backgroundColor: "rgb(243, 248, 251)",
        },
        externalWidget: {
            // Their booking embed is tall; without this the page jumps as it mounts.
            minHeight: "600px",
        },
    },
    // No framing. There was a "BOOK YOUR ESTIMATE" tab and a bordered card around every step,
    // matching CostGuide's design as it stood when this preset was written. Their own form no
    // longer has either (match.costguide.com, checked 2026-09-20): it is a plain card with the
    // fields on it. Ours read as a box inside a box -- the widget's own rounded container, then
    // the tab, then a second squared border inside that.
    //
    // FormDisplay renders `framing ? <FormFrame> : stepDisplay`, so leaving this off is what
    // removes the tab AND the inner card; there is no separate switch for either.
};
